import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Card, 
  CardContent,
  Chip
} from '@mui/material';
import { AutoFixHigh, Psychology, TrendingUp } from '@mui/icons-material';

interface QueryAttempt {
  attempt: number;
  issue: string;
  solution?: string;
  sql?: string;
  executionTime?: number;
}

interface QueryAttemptsPanelProps {
  attempts: QueryAttempt[];
  onShowDetails?: (attempt: QueryAttempt) => void;
}

export function QueryAttemptsPanel({ attempts, onShowDetails }: QueryAttemptsPanelProps) {
  if (!attempts || attempts.length === 0) {
    return null;
  }

  const successfulAttempt = attempts.find(a => a.solution);
  const totalAttempts = attempts.length;

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoFixHigh sx={{ mr: 1, fontSize: '1rem', color: 'warning.main' }} />
          Query Building Journey
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            I had to try {totalAttempts} different approach{totalAttempts > 1 ? 'es' : ''} to get this right:
          </Typography>
        </Box>

        {attempts.map((attempt, index) => (
          <Card 
            key={index} 
            sx={{ 
              mt: 1, 
              border: attempt.solution ? '2px solid #4caf50' : '1px solid #e0e0e0',
              backgroundColor: attempt.solution ? '#f1f8e9' : 'background.paper'
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Attempt {attempt.attempt}
                </Typography>
                {attempt.solution && (
                  <Chip 
                    label="Success" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    icon={<TrendingUp />}
                  />
                )}
              </Box>
              
              <Typography variant="body2" color="error" gutterBottom>
                <strong>Issue:</strong> {attempt.issue}
              </Typography>
              
              {attempt.solution && (
                <Typography variant="body2" color="success.main" gutterBottom>
                  <strong>Solution:</strong> {attempt.solution}
                </Typography>
              )}
              
              {attempt.executionTime && (
                <Typography variant="caption" color="text.secondary">
                  Execution time: {attempt.executionTime}ms
                </Typography>
              )}
              
              {attempt.sql && onShowDetails && (
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label="View SQL" 
                    size="small" 
                    variant="outlined"
                    onClick={() => onShowDetails(attempt)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        ))}

        {successfulAttempt && (
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
            <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
              <Psychology sx={{ mr: 1, fontSize: '1rem' }} />
              <strong>Learning Note:</strong> I've learned from this interaction and will remember it for next time!
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
